import { ref, onMounted, onUnmounted } from 'vue'
import type { Ref } from 'vue'
import type { NavigationItem } from '@/types/umbraco'
import { useI18n } from '@/composables/useI18n'

export interface HeaderState {
  // Reactive state
  isCompact: Ref<boolean>
  isMobileMenuOpen: Ref<boolean>
  activeDropdown: Ref<number | null>
  scrollY: Ref<number>
  isLoading: Ref<boolean>
  hasError: Ref<boolean>
  navigationItems: Ref<NavigationItem[]>
  logoUrl: Ref<string | null>
  
  // Methods
  toggleMobileMenu: () => void
  closeMobileMenu: () => void
  toggleDropdown: (index: number) => void
  closeDropdowns: () => void
  handleScroll: () => void
  handleClickOutside: (event: Event) => void
  handleKeydown: (event: KeyboardEvent) => void
  
  // Internationalization
  $t: (key: string, fallback?: string) => string
}

/**
 * Header composable with sticky behavior and responsive navigation
 * Manages scroll-based compact mode, mobile menu, and dropdown functionality
 */
export default function useHeader(): HeaderState {
  // Get internationalization
  const { t } = useI18n()
  
  // Reactive state
  const isCompact = ref<boolean>(false)
  const isMobileMenuOpen = ref<boolean>(false)
  const activeDropdown = ref<number | null>(null)
  const scrollY = ref<number>(0)
  const isLoading = ref<boolean>(true)
  const hasError = ref<boolean>(false)
  const navigationItems = ref<NavigationItem[]>([])
  const logoUrl = ref<string | null>(null)
  
  // Configuration
  const COMPACT_THRESHOLD = 100
  const MOBILE_BREAKPOINT = 1024

  // Compute and expose header height via CSS variable for use in styles
  const updateHeaderHeightVar = (): void => {
    const header = document.querySelector<HTMLElement>('.site-header')
    const height = header?.offsetHeight ?? 64
    document.documentElement.style.setProperty('--site-header-height', `${height}px`)
  }
  
  /**
   * Handle scroll events for compact header behavior
   */
  const handleScroll = (): void => {
    scrollY.value = window.scrollY
    isCompact.value = scrollY.value > COMPACT_THRESHOLD
    // Update header height variable in case compact state changed the height
    updateHeaderHeightVar()
  }
  
  /**
   * Toggle mobile menu visibility
   */
  const toggleMobileMenu = (): void => {
    // Update header height var before opening/closing
    updateHeaderHeightVar()

    isMobileMenuOpen.value = !isMobileMenuOpen.value
    
    // Close any open dropdowns when opening mobile menu
    if (isMobileMenuOpen.value) {
      closeDropdowns()
    }
    
    // Prevent body scroll when mobile menu is open
    if (isMobileMenuOpen.value) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    // Recompute after layout settles
    requestAnimationFrame(updateHeaderHeightVar)
  }
  
  /**
   * Close mobile menu
   */
  const closeMobileMenu = (): void => {
    isMobileMenuOpen.value = false
    document.body.style.overflow = ''
    closeDropdowns()
  }
  
  /**
   * Toggle dropdown menu
   */
  const toggleDropdown = (index: number): void => {
    if (activeDropdown.value === index) {
      activeDropdown.value = null
    } else {
      activeDropdown.value = index
    }
  }
  
  /**
   * Close all dropdown menus
   */
  const closeDropdowns = (): void => {
    activeDropdown.value = null
  }
  
  /**
   * Handle clicks outside the navigation to close menus
   */
  const handleClickOutside = (event: Event): void => {
    const target = event.target as HTMLElement
    const header = document.querySelector('.site-header')
    
    if (header && !header.contains(target)) {
      closeDropdowns()
      
      // Close mobile menu if clicking outside on mobile
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        closeMobileMenu()
      }
    }
  }
  
  /**
   * Handle keyboard navigation
   */
  const handleKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case 'Escape':
        closeDropdowns()
        closeMobileMenu()
        break
      case 'Tab':
        // Handle tab navigation within dropdowns
        if (activeDropdown.value !== null) {
          // Let the browser handle tab navigation
          // Additional logic could be added here for enhanced accessibility
        }
        break
    }
  }

  // Resize handler (needs to be the same reference for add/remove)
  const handleResize = (): void => {
    updateHeaderHeightVar()
    if (window.innerWidth > MOBILE_BREAKPOINT && isMobileMenuOpen.value) {
      closeMobileMenu()
    }
  }
  
  /**
   * Fetch navigation data from Umbraco
   */
  const fetchNavigationData = async (): Promise<void> => {
    try {
      isLoading.value = true
      hasError.value = false

      // Helper: normalize dropdown item arrays from various shapes (array or JSON string)
      const normalizeDropdownItems = (raw: unknown): { title: string; url: string }[] => {
        if (!raw) return []

        let arr: unknown = raw
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw)
            arr = parsed
          } catch {
            return []
          }
        }

        if (!Array.isArray(arr)) return []

        return (arr as any[])
          .map((item) => {
            if (!item) return null
            if (typeof item === 'string') {
              // Unsupported shape (string only) – skip
              return null
            }
            const title = item.title ?? item.Text ?? item.name ?? item.Label ?? item.label ?? ''
            const url = item.url ?? item.href ?? item.Url ?? item.link ?? ''
            if (!title || !url) return null
            return { title: String(title), url: String(url) }
          })
          .filter((x): x is { title: string; url: string } => Boolean(x))
      }

      // Helper: normalize single navigation item
      const normalizeNavItem = (nav: any): NavigationItem => {
        const dropdownItems = normalizeDropdownItems(
          nav?.dropdownItems ?? nav?.items ?? nav?.children ?? nav?.DropdownItems
        )
        return {
          title: nav?.title ?? nav?.text ?? nav?.name ?? '',
          url: nav?.url ?? nav?.href ?? nav?.Url ?? undefined,
          dropdownItems: dropdownItems.length ? dropdownItems : undefined
        }
      }

      // Get current language from cookie for API call
      const getCookieValue = (name: string): string | null => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
        return match ? match[2] : null
      }
      const langFromCookie = getCookieValue('preferredLanguage')
      const culture = langFromCookie === 'nl' ? 'nl-nl' : 'en-us'
      
      const response = await fetch(`/api/content/navigation?culture=${culture}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Normalize response shape to what the component expects
      const rawItems = data?.navigationItems ?? data?.items ?? []
      navigationItems.value = Array.isArray(rawItems)
        ? rawItems.map(normalizeNavItem)
        : []

      // Prefer logoUrl, fall back to logo, then siteLogo
      logoUrl.value = data?.logoUrl || data?.logo || data?.siteLogo || null

    } catch (error) {
      console.warn('❌ Failed to fetch navigation data, using fallback:', error)
      hasError.value = true

      // Use fallback navigation immediately
      navigationItems.value = [
        {
          title: 'About',
          dropdownItems: [
            { title: 'Game', url: '/game' },
            { title: 'Us', url: '/us' }
          ]
        },
        { title: 'FAQ', url: '/faq' },
        { title: 'Roadmap', url: '/roadmap' },
        { title: 'Playtesting', url: '/playtesting' },
        { title: 'Socials', url: '/socials' }
      ]

      // Use fallback logo
      logoUrl.value = null // This will show the text logo
    } finally {
      isLoading.value = false
    }
  }
  
  // Setup event listeners and fetch data on mount
  onMounted(() => {
    // Initial scroll position
    handleScroll()

    // Initialize header height CSS var
    updateHeaderHeightVar()
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    
    // Fetch navigation data
    fetchNavigationData()
  })
  
  // Cleanup event listeners on unmount
  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
    
    // Restore body scroll
    document.body.style.overflow = ''
  })
  
  return {
    // State
    isCompact,
    isMobileMenuOpen,
    activeDropdown,
    scrollY,
    isLoading,
    hasError,
    navigationItems,
    logoUrl,
    
    // Methods
    toggleMobileMenu,
    closeMobileMenu,
    toggleDropdown,
    closeDropdowns,
    handleScroll,
    handleClickOutside,
    handleKeydown,
    
    // Internationalization
    $t: t
  }
}
